import React, { useState, useEffect } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  ScrollView, 
  TouchableOpacity, 
  Alert, 
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { collection, addDoc } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { MUSCLE_GROUPS } from '../../constants/muscleGroups';
import { Exercise } from '../../types';
import { useTheme } from '../../context/ThemeContext';
import { Ionicons } from '@expo/vector-icons';

type Props = NativeStackScreenProps<MainStackParamList, 'CreateExercise'>;

const CreateExerciseScreen = ({ navigation }: Props) => {
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [targetMuscles, setTargetMuscles] = useState<string[]>([]);
  const [difficultyLevel, setDifficultyLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('intermediate');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Establecer título y botón de retroceso en la navegación en el primer renderizado
  useEffect(() => {
    navigation.setOptions({
      title: 'Crear Ejercicio',
      headerLeft: () => (
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.navigate('MainTabs', {
            screen: 'Exercises',
            initial: false
          })}
        >
          <Ionicons name="arrow-back" size={24} color={theme.colors.text} />
        </TouchableOpacity>
      )
    });
  }, [navigation, theme]);

  const handleMuscleToggle = (muscle: string) => {
    if (targetMuscles.includes(muscle)) {
      setTargetMuscles(targetMuscles.filter(m => m !== muscle));
    } else {
      setTargetMuscles([...targetMuscles, muscle]);
    }
  };

  const validateForm = () => {
    if (!name.trim()) {
      setErrorMessage('Por favor ingresa un nombre para el ejercicio');
      return false;
    }

    if (!instructions.trim()) {
      setErrorMessage('Por favor ingresa instrucciones');
      return false;
    }

    if (targetMuscles.length === 0) {
      setErrorMessage('Por favor selecciona al menos un grupo muscular');
      return false;
    }

    setErrorMessage(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }

      const exerciseData = {
        name,
        description,
        instructions,
        muscleGroups: targetMuscles.map(m => MUSCLE_GROUPS.find(group => group.id === m)?.id).filter(Boolean),
        difficultyLevel,
        createdBy: userId,
        createdAt: new Date(),
      } as Exercise;

      const docRef = await addDoc(collection(firestore, 'exercises'), exerciseData);

      // Al completar con éxito, mostrar mensaje y volver al listado de ejercicios
      Alert.alert(
        'Éxito',
        'Ejercicio creado correctamente',
        [
          {
            text: 'OK',
            onPress: () => {
              // Navegar explícitamente a la pantalla de Ejercicios
              navigation.navigate('MainTabs', {
                screen: 'Exercises',
                initial: false
              });
            }
          }
        ]
      );
    } catch (error) {
      console.error('Error al crear ejercicio:', error);
      setErrorMessage('No se pudo crear el ejercicio. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView style={[styles.scrollView, { backgroundColor: theme.colors.background }]}>
        <View style={styles.container}>
          {/* Botón de navegación en UI para volver al listado */}
          <TouchableOpacity
            style={[styles.navigationButton, { backgroundColor: theme.colors.card }]}
            onPress={() => navigation.navigate('MainTabs', {
              screen: 'Exercises',
              initial: false
            })}
          >
            <Ionicons name="arrow-back" size={20} color={theme.colors.primary} />
            <Text style={[styles.navigationButtonText, { color: theme.colors.primary }]}>
              Volver al listado de ejercicios
            </Text>
          </TouchableOpacity>

          <Card style={styles.card}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Información Básica</Text>
            
            <Input
              label="Nombre del Ejercicio"
              value={name}
              onChangeText={setName}
              placeholder="Ej. Press de Banca"
              darkMode={true}
            />
            
            <Input
              label="Descripción (opcional)"
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={3}
              placeholder="Breve descripción del ejercicio..."
              darkMode={true}
            />
            
            <Input
              label="Instrucciones"
              value={instructions}
              onChangeText={setInstructions}
              multiline
              numberOfLines={4}
              placeholder="Pasos detallados para realizar el ejercicio correctamente..."
              darkMode={true}
            />
          </Card>
          
          <Card style={styles.card}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Grupos Musculares</Text>
            <Text style={[styles.sectionSubtitle, { color: theme.colors.grey.dark }]}>Selecciona los grupos musculares que trabaja este ejercicio</Text>
            
            <View style={styles.muscleGroupsContainer}>
              {MUSCLE_GROUPS.map((muscle) => (
                <TouchableOpacity
                  key={muscle.id}
                  style={[
                    styles.muscleTag,
                    { backgroundColor: theme.colors.grey.light },
                    targetMuscles.includes(muscle.id) && { backgroundColor: theme.colors.primary }
                  ]}
                  onPress={() => handleMuscleToggle(muscle.id)}
                >
                  <Text
                    style={[
                      styles.muscleTagText,
                      { color: theme.colors.text },
                      targetMuscles.includes(muscle.id) && { color: theme.colors.white }
                    ]}
                  >
                    {muscle.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </Card>
          
          <Card style={styles.card}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Nivel de Dificultad</Text>
            
            <View style={styles.difficultyContainer}>
              <TouchableOpacity
                style={[
                  styles.difficultyButton,
                  { backgroundColor: theme.colors.grey.light },
                  difficultyLevel === 'beginner' && { backgroundColor: theme.colors.primary }
                ]}
                onPress={() => setDifficultyLevel('beginner')}
              >
                <Text
                  style={[
                    styles.difficultyButtonText,
                    { color: theme.colors.text },
                    difficultyLevel === 'beginner' && { color: theme.colors.white }
                  ]}
                >
                  Principiante
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[
                  styles.difficultyButton,
                  { backgroundColor: theme.colors.grey.light },
                  difficultyLevel === 'intermediate' && { backgroundColor: theme.colors.primary }
                ]}
                onPress={() => setDifficultyLevel('intermediate')}
              >
                <Text
                  style={[
                    styles.difficultyButtonText,
                    { color: theme.colors.text },
                    difficultyLevel === 'intermediate' && { color: theme.colors.white }
                  ]}
                >
                  Intermedio
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[
                  styles.difficultyButton,
                  { backgroundColor: theme.colors.grey.light },
                  difficultyLevel === 'advanced' && { backgroundColor: theme.colors.primary }
                ]}
                onPress={() => setDifficultyLevel('advanced')}
              >
                <Text
                  style={[
                    styles.difficultyButtonText,
                    { color: theme.colors.text },
                    difficultyLevel === 'advanced' && { color: theme.colors.white }
                  ]}
                >
                  Avanzado
                </Text>
              </TouchableOpacity>
            </View>
          </Card>
          
          {/* Error Message */}
          {errorMessage && (
            <View style={[styles.errorContainer, { backgroundColor: theme.colors.error, marginBottom: spacing.md }]}>
              <Text style={[styles.errorText, { color: theme.colors.white }]}>
                {errorMessage}
              </Text>
            </View>
          )}
          
          {/* Submit Button */}
          <Button
            title="Crear Ejercicio"
            onPress={handleSubmit}
            type="primary"
            loading={loading}
            style={styles.submitButton}
          />
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  container: {
    padding: spacing.lg,
  },
  backButton: {
    padding: spacing.sm,
  },
  navigationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
  },
  navigationButtonText: {
    marginLeft: spacing.xs,
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  card: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginBottom: spacing.md,
  },
  sectionSubtitle: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.md,
  },
  muscleGroupsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  muscleTag: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
    marginRight: spacing.xs,
    marginBottom: spacing.xs,
  },
  muscleTagSelected: {
    backgroundColor: colors.primary,
  },
  muscleTagText: {
    fontSize: typography.fontSize.sm,
  },
  muscleTagTextSelected: {
    color: colors.white,
  },
  difficultyContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  difficultyButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    marginHorizontal: spacing.xs,
    borderRadius: borderRadius.md,
  },
  difficultyButtonSelected: {
    backgroundColor: colors.primary,
  },
  difficultyButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  difficultyButtonTextSelected: {
    color: colors.white,
  },
  errorContainer: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.sm,
  },
  errorText: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
  },
  submitButton: {
    marginTop: spacing.md,
  },
});

export default CreateExerciseScreen; 